import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Modal from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Wrench,
  Search,
  Loader2,
  X,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Clock,
  Barcode,
} from "lucide-react";
import apiClient from "@/api/apiClient";
import { API_ENDPOINTS } from "@/api/header";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";

const isRepairPaymentPending = (repair) => {
  if (!repair) return false;
  const pStatus = (repair.payment_status || "pending").toLowerCase();
  if (
    pStatus === "completed" ||
    pStatus === "paid" ||
    pStatus === "cancelled" ||
    pStatus === "refunded"
  ) {
    return false;
  }
  const balanceDue =
    repair.balance_due != null ? parseFloat(repair.balance_due) : null;
  const paidAmount = parseFloat(repair.paid_amount || 0);
  const totalCost = parseFloat(repair.total_cost ?? repair.estimated_cost ?? 0);
  if (balanceDue !== null && balanceDue <= 0 && (paidAmount > 0 || totalCost > 0)) {
    return false;
  }
  const jobStatus = (
    typeof repair.status === "string" ? repair.status : repair.status?.value || ""
  ).toLowerCase();
  if (jobStatus === "cancelled") return false;
  return true;
};

const getRepairBalanceDue = (repair) => {
  if (!repair) return 0;
  const balanceDueVal =
    repair.balance_due != null ? parseFloat(repair.balance_due) : null;
  const totalVal = parseFloat(repair.total_cost ?? repair.estimated_cost ?? 0);
  const paymentStatus = (repair.payment_status || "").toLowerCase();
  const isPaid = paymentStatus === "completed" || paymentStatus === "paid";
  if (balanceDueVal != null) return Math.max(0, balanceDueVal);
  if (isPaid) return 0;
  const paidVal = parseFloat(repair.paid_amount || 0);
  return Math.max(0, parseFloat((totalVal - paidVal).toFixed(2)));
};

// Helper for priority badge aligning with priority field object/string
const getPriorityBadge = (priority) => {
  if (!priority) return null;
  const priorityValue = (
    typeof priority === "object" ? priority.value || priority.label : priority
  ) || "normal";
  const priorityLabel = (
    typeof priority === "object" ? priority.label || priority.value : priority
  ) || "Normal";
  const tooltip = typeof priority === "object" ? priority.description : undefined;

  const valLower = String(priorityValue).toLowerCase();
  let badgeStyle = "bg-muted/50 text-foreground border-border/60";
  if (valLower === "high") {
    badgeStyle = "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30";
  } else if (valLower === "urgent" || valLower === "critical") {
    badgeStyle = "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30";
  } else if (valLower === "low") {
    badgeStyle = "bg-muted/40 text-muted-foreground border-border/40";
  }

  return (
    <Badge
      variant="outline"
      title={tooltip}
      className={`text-[10px] py-0 h-4 font-semibold capitalize ${badgeStyle}`}
    >
      {priorityLabel}
    </Badge>
  );
};

// Helper for job status badge
const getJobStatusBadge = (rawStatus) => {
  if (!rawStatus) return null;
  const statusStr = (
    typeof rawStatus === "string"
      ? rawStatus
      : rawStatus?.value || "pending"
  ).toLowerCase();

  const label =
    (typeof rawStatus === "object" && rawStatus?.label)
      ? rawStatus.label
      : statusStr === "ready_for_pickup"
        ? "Ready for Pickup"
        : statusStr === "in_progress"
          ? "In Progress"
          : statusStr === "awaiting_parts"
            ? "Awaiting Parts"
            : statusStr.charAt(0).toUpperCase() + statusStr.slice(1);

  const tooltip = typeof rawStatus === "object" ? rawStatus?.description : undefined;

  if (
    statusStr === "completed" ||
    statusStr === "delivered" ||
    statusStr === "ready" ||
    statusStr === "ready_for_pickup"
  ) {
    return (
      <Badge
        variant="outline"
        title={tooltip}
        className="text-[10px] py-0 h-4 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 font-semibold capitalize"
      >
        {label}
      </Badge>
    );
  }

  if (statusStr === "cancelled" || statusStr === "rejected") {
    return (
      <Badge
        variant="destructive"
        title={tooltip}
        className="text-[10px] py-0 h-4 font-semibold capitalize"
      >
        {label}
      </Badge>
    );
  }

  if (
    statusStr === "in_progress" ||
    statusStr === "diagnosing" ||
    statusStr === "testing"
  ) {
    return (
      <Badge
        variant="outline"
        title={tooltip}
        className="text-[10px] py-0 h-4 bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30 font-semibold capitalize"
      >
        {label}
      </Badge>
    );
  }

  return (
    <Badge
      variant="warning"
      title={tooltip}
      className="text-[10px] py-0 h-4 bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30 font-semibold capitalize"
    >
      {label}
    </Badge>
  );
};

// Helper for payment status badge
const getPaymentStatusBadge = (repair) => {
  if (!repair) return null;
  const pStatus = (repair.payment_status || "pending").toLowerCase();
  const totalVal = parseFloat(repair.total_cost ?? repair.estimated_cost ?? 0);
  const paidVal = parseFloat(repair.paid_amount || 0);
  const balance = getRepairBalanceDue(repair);

  // 1. Explicitly Paid / Completed, or has total cost and fully paid with 0 balance
  if (
    pStatus === "paid" ||
    pStatus === "completed" ||
    (totalVal > 0 && paidVal >= totalVal && balance <= 0.01)
  ) {
    return (
      <Badge
        variant="outline"
        className="text-[10px] py-0 h-4 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 bg-emerald-500/10 font-semibold"
      >
        Paid
      </Badge>
    );
  }

  // 2. Partially Paid
  if (
    pStatus === "partially-paid" ||
    pStatus === "partially_paid" ||
    pStatus === "partial" ||
    (paidVal > 0.01 && balance > 0.01)
  ) {
    return (
      <Badge
        variant="outline"
        className="text-[10px] py-0 h-4 bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30 font-semibold"
      >
        Partially Paid
      </Badge>
    );
  }

  // 3. Cancelled or Refunded
  if (pStatus === "cancelled" || pStatus === "refunded") {
    return (
      <Badge
        variant="destructive"
        className="text-[10px] py-0 h-4 font-semibold capitalize"
      >
        {pStatus}
      </Badge>
    );
  }

  // 4. Pending Payment (Default when pending, even if total cost / balance is 0.00)
  return (
    <Badge
      variant="outline"
      className="text-[10px] py-0 h-4 bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30 font-semibold"
    >
      Pending Payment
    </Badge>
  );
};

const PosRepairsModal = ({
  show,
  onHide,
  currencySymbol = "$",
  onSelectRepair,
  mode = "pos",
}) => {
  const navigate = useNavigate();
  const currentStore = useSelector((state) => state.current_store);

  // Filters & State
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [repairsList, setRepairsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectingRepairId, setSelectingRepairId] = useState(null);
  const searchInputRef = useRef(null);
  const handleBarcodeOrSearchSubmitRef = useRef(null);
  const [paginationMeta, setPaginationMeta] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Fetch repairs
  const fetchRepairs = useCallback(async () => {
    if (!show) return;
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: 15,
        perPage: 15,
        sort_by: "created_at",
        sort_order: "desc",
        with: ["customer", "technician", "repairCategories"],
      };
      if (currentStore?.id) params.store_id = currentStore.id;
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter !== "all") params.status = statusFilter;

      const res = await apiClient.get(API_ENDPOINTS.REPAIRS.JOBS, { params });
      let list = res.data?.data || [];

      // Filter by payment if paymentFilter selected
      if (paymentFilter === "pending") {
        list = list.filter((r) => isRepairPaymentPending(r));
      } else if (paymentFilter === "paid") {
        list = list.filter((r) => !isRepairPaymentPending(r));
      }

      setRepairsList(list);
      setPaginationMeta({
        current_page: res.data?.meta?.current_page || currentPage,
        last_page: res.data?.meta?.last_page || 1,
        per_page: res.data?.meta?.per_page || 15,
        total: res.data?.meta?.total || list.length,
      });
    } catch (err) {
      console.error("Fetch repairs error:", err);
      toast.error("Failed to load repair jobs");
    } finally {
      setIsLoading(false);
    }
  }, [
    show,
    currentPage,
    currentStore?.id,
    debouncedSearch,
    statusFilter,
    paymentFilter,
  ]);

  useEffect(() => {
    if (show) {
      fetchRepairs();
    }
  }, [show, fetchRepairs]);

  // Load repair job handler
  const handleLoadRepair = async (repair) => {
    if (selectingRepairId) return;
    setSelectingRepairId(repair.id);
    try {
      let repairToLoad = repair;
      try {
        const detailRes = await apiClient.get(
          API_ENDPOINTS.REPAIRS.REPAIR_DETAILS(repair.id),
        );
        if (detailRes.data?.data) {
          repairToLoad = detailRes.data.data;
        } else if (detailRes.data?.id) {
          repairToLoad = detailRes.data;
        }
      } catch (err) {
        console.warn("Could not fetch full repair details, using list item:", err);
      }

      if (mode === "repair" && onSelectRepair) {
        onSelectRepair(repairToLoad);
        toast.success(
          `Repair #${repairToLoad.repair_number || repairToLoad.id} loaded into Repair POS`,
        );
        onHide();
      } else {
        toast.info(
          `Loading Repair #${repairToLoad.repair_number || repairToLoad.id} into Repair POS...`,
        );
        onHide();
        navigate(`/pos/repair?existing_id=${repairToLoad.id}`);
      }
    } finally {
      setSelectingRepairId(null);
    }
  };

  // Barcode Scan / Immediate Search Submission
  const handleBarcodeOrSearchSubmit = async (rawCode) => {
    const code = (rawCode || "").trim();
    if (!code) return;
    const cleanCode = code.replace(/^#/, "").trim();
    if (!cleanCode) return;

    setIsLoading(true);
    try {
      const lower = cleanCode.toLowerCase();

      // 1. Check in-memory list first
      let matchedRepair = repairsList.find((r) => {
        const repNo = (r.repair_number || "").toLowerCase().replace(/^#/, "");
        const serNo = (r.serial_number || "").toLowerCase();
        return (
          repNo === lower ||
          String(r.id) === cleanCode ||
          (serNo && serNo === lower)
        );
      });

      // 2. Query repairs API
      if (!matchedRepair) {
        const params = {
          page: 1,
          per_page: 5,
          search: cleanCode,
          with: ["customer", "technician", "repairCategories"],
        };
        if (currentStore?.id) params.store_id = currentStore.id;

        const res = await apiClient.get(API_ENDPOINTS.REPAIRS.JOBS, { params });
        const list = res.data?.data || [];

        matchedRepair = list.find((r) => {
          const repNo = (r.repair_number || "").toLowerCase().replace(/^#/, "");
          const serNo = (r.serial_number || "").toLowerCase();
          return (
            repNo === lower ||
            String(r.id) === cleanCode ||
            (serNo && serNo === lower)
          );
        });

        if (!matchedRepair && list.length === 1) {
          matchedRepair = list[0];
        }
      }

      // 3. Fallback direct ID lookup
      if (!matchedRepair) {
        try {
          const detailRes = await apiClient.get(
            API_ENDPOINTS.REPAIRS.REPAIR_DETAILS(cleanCode),
          );
          if (detailRes.data?.data) matchedRepair = detailRes.data.data;
          else if (detailRes.data) matchedRepair = detailRes.data;
        } catch {
          // not direct id
        }
      }

      // 4. If record is available, load it into POS / Repair POS
      if (matchedRepair) {
        handleLoadRepair(matchedRepair);
        return;
      }

      // 5. Unless no -> notify user that no record is available
      toast.error(`No repair found matching barcode "${code}"`);
    } catch (err) {
      console.error("Barcode repair scan error:", err);
      toast.error(`Failed to lookup repair for barcode "${code}"`);
    } finally {
      setIsLoading(false);
    }
  };

  handleBarcodeOrSearchSubmitRef.current = handleBarcodeOrSearchSubmit;

  // Auto-focus search input when modal opens
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [show]);

  // Global barcode scanner listener when modal is open
  useEffect(() => {
    if (!show) return;
    let scanBuffer = "";
    let lastKeyTime = 0;

    const handleGlobalScan = (e) => {
      if (document.activeElement === searchInputRef.current) {
        return;
      }

      const now = Date.now();
      if (e.key === "Enter" || e.keyCode === 13) {
        if (scanBuffer.length >= 2 && now - lastKeyTime < 250) {
          e.preventDefault();
          const code = scanBuffer.trim();
          scanBuffer = "";
          if (code) {
            handleBarcodeOrSearchSubmitRef.current?.(code);
          }
        } else {
          scanBuffer = "";
        }
        return;
      }

      if (e.key && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        if (now - lastKeyTime > 80) {
          scanBuffer = e.key;
        } else {
          scanBuffer += e.key;
        }
        lastKeyTime = now;
      }
    };

    window.addEventListener("keydown", handleGlobalScan);
    return () => window.removeEventListener("keydown", handleGlobalScan);
  }, [show]);

  return (
    <Modal
      open={show}
      onOpenChange={(v) => !v && onHide()}
      title="Repairs"
      icon={Wrench}
      maxWidth="max-w-5xl"
      footer={
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 w-full">
          <div className="text-xs text-muted-foreground">
            Total repairs: {paginationMeta.total}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={paginationMeta.current_page <= 1 || isLoading}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>
            <span className="text-xs text-muted-foreground font-mono">
              {paginationMeta.current_page} / {paginationMeta.last_page}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={
                paginationMeta.current_page >= paginationMeta.last_page ||
                isLoading
              }
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={onHide}>
              Close
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3.5 py-1">
        {/* Search & Filter Bar */}
        <div className="flex flex-col md:flex-row gap-2.5 items-stretch md:items-center justify-between">
          <div className="relative flex-1">
            <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Scan barcode or search by Repair # (e.g. REP-001), Customer, Serial..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.keyCode === 13) {
                  e.preventDefault();
                  handleBarcodeOrSearchSubmit(searchTerm);
                }
              }}
              className="pl-9 h-8 text-xs bg-muted/20"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 text-muted-foreground"
                onClick={() => setSearchTerm("")}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Select
              value={statusFilter}
              onValueChange={(val) => {
                setStatusFilter(val);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="h-8 text-xs w-36">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="diagnosing">Diagnosing</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="awaiting_parts">Awaiting Parts</SelectItem>
                <SelectItem value="ready_for_pickup">
                  Ready for Pickup
                </SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={paymentFilter}
              onValueChange={(val) => {
                setPaymentFilter(val);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="h-8 text-xs w-36">
                <SelectValue placeholder="Payment Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="pending">Payment Pending</SelectItem>
                <SelectItem value="paid">Fully Paid</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={fetchRepairs}
              title="Refresh repairs"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="border rounded-xl overflow-hidden min-h-[320px] max-h-[500px] overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-xs">Loading repair jobs...</p>
            </div>
          ) : repairsList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-2 text-center">
              <Wrench className="h-10 w-10 text-muted-foreground/40" />
              <p className="text-sm font-medium text-foreground">
                No repair jobs found
              </p>
              <p className="text-xs">
                {debouncedSearch
                  ? `No repairs matching "${debouncedSearch}"`
                  : "There are currently no repair jobs registered for this store."}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="text-xs bg-muted/30">
                  <TableHead className="font-semibold">Repair #</TableHead>
                  <TableHead className="font-semibold">Customer</TableHead>
                  <TableHead className="font-semibold">Repair Details</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Payment</TableHead>
                  <TableHead className="text-right font-semibold">
                    Cost / Total
                  </TableHead>
                  <TableHead className="text-right font-semibold w-24">
                    Action
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {repairsList.map((repair) => {
                  const totalCost = parseFloat(
                    repair.total_cost ?? repair.estimated_cost ?? 0,
                  );
                  const paidAmount = parseFloat(repair.paid_amount || 0);
                  const balanceDue = getRepairBalanceDue(repair);

                  return (
                    <TableRow
                      key={repair.id}
                      className="text-xs hover:bg-muted/30"
                    >
                      {/* Repair Job # */}
                      <TableCell>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono font-bold text-foreground">
                            {repair.repair_number || `#${repair.id}`}
                          </span>
                        </div>
                        <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                          <Clock className="h-3 w-3" />
                          {formatDate(repair.created_at)}
                        </div>
                        {repair.estimated_completion_date && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            Est: {formatDate(repair.estimated_completion_date)}
                          </div>
                        )}
                      </TableCell>

                      {/* Customer */}
                      <TableCell>
                        <div className="font-semibold text-foreground truncate max-w-[150px]">
                          {repair.customer?.full_name ||
                            repair.customer?.name ||
                            (repair.customer_id === "00000000-0000-0000-0000-000000000001"
                              ? "Walk-in Customer"
                              : repair.customer_id
                                ? "Walk-in Customer"
                                : "Walk-in Customer")}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate max-w-[150px]">
                          {repair.customer?.phone ||
                            repair.customer?.email ||
                            (repair.customer_id === "00000000-0000-0000-0000-000000000001"
                              ? "Walk-in"
                              : "No contact info")}
                        </div>
                      </TableCell>

                      {/* Repair Details: item_description, issue_description, serial_number, priority */}
                      <TableCell>
                        <div
                          className="font-medium text-foreground truncate max-w-[200px]"
                          title={repair.item_description || "Repair Item"}
                        >
                          {repair.item_description || "Repair Item"}
                        </div>

                        <div className="flex items-center gap-1.5 flex-wrap mt-1">
                          {repair.serial_number && (
                            <span
                              className="text-[10px] font-mono text-muted-foreground bg-muted/70 px-1 py-0.2 rounded border border-border/50"
                              title={`Serial Number: ${repair.serial_number}`}
                            >
                              SN: {repair.serial_number}
                            </span>
                          )}
                          {repair.priority && getPriorityBadge(repair.priority)}
                        </div>
                      </TableCell>

                      {/* Status */}
                      <TableCell>{getJobStatusBadge(repair.status)}</TableCell>

                      {/* Payment */}
                      <TableCell>{getPaymentStatusBadge(repair)}</TableCell>

                      {/* Total / Balance */}
                      <TableCell className="text-right">
                        <div className="font-mono font-bold text-foreground">
                          {currencySymbol}
                          {totalCost.toFixed(2)}
                        </div>
                        {balanceDue > 0.01 ? (
                          <div className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold">
                            Due: {currencySymbol}
                            {balanceDue.toFixed(2)}
                          </div>
                        ) : paidAmount > 0.01 ? (
                          <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                            Paid: {currencySymbol}
                            {paidAmount.toFixed(2)}
                          </div>
                        ) : (
                          (parseFloat(repair.labor_cost || 0) > 0 ||
                            parseFloat(repair.parts_cost || 0) > 0) && (
                            <div className="text-[10px] text-muted-foreground">
                              L: {currencySymbol}
                              {parseFloat(repair.labor_cost || 0).toFixed(2)} | P:{" "}
                              {currencySymbol}
                              {parseFloat(repair.parts_cost || 0).toFixed(2)}
                            </div>
                          )
                        )}
                      </TableCell>

                      {/* Single Action Button: "Select" */}
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          className="h-7 px-3 text-xs font-semibold cursor-pointer"
                          onClick={() => handleLoadRepair(repair)}
                          disabled={selectingRepairId === repair.id}
                        >
                          {selectingRepairId === repair.id ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              Selecting...
                            </>
                          ) : (
                            "Select"
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </Modal>
  );
};

PosRepairsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  currencySymbol: PropTypes.string,
  onSelectRepair: PropTypes.func,
  mode: PropTypes.string,
};

export default PosRepairsModal;
